import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  FormControl,
  IconButton,
  InputAdornment,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import SearchIcon from '@mui/icons-material/Search';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import dayjs from 'dayjs';
import { PageHeaderBar } from '../../../components/layout/PageHeaderBar';
import { appShellHeaderTitleSx } from '../../../components/layout/layoutConstants';
import Pagination from '../../../components/atoms/Pagination';
import withComponentAuth from '../../../hoc/withComponentAuth';
import { dashboardTokens } from '../../Dashboard/Components/dashboardCardStyles';
import { AdminAvatar } from '../components/AdminAvatar';
import { LeadsListTable } from '../components/LeadsListTable';
import { leadStatusMeta, platformMeta } from '../components/leadsMeta';
import {
  AdPlatform,
  Campaign,
  MarketingLead,
  MarketingLeadStatus,
  getMarketingLeadCampaigns,
  getMarketingLeads,
} from '../../../services/marketingLeads';

const PLATFORM_OPTIONS: AdPlatform[] = ['META', 'GOOGLE', 'ORGANIC', 'OTHER'];
const STATUS_OPTIONS: MarketingLeadStatus[] = [
  'NEW',
  'CONTACTED',
  'QUALIFIED',
  'CONVERTED',
  'LOST',
];

const selectSx = {
  minWidth: 150,
  color: '#e2e8f0',
  bgcolor: '#1c1c1c',
  '.MuiOutlinedInput-notchedOutline': { borderColor: dashboardTokens.border },
  '.MuiSvgIcon-root': { color: '#94a3b8' },
};

const escapeCsvCell = (value: string) => `"${value.replace(/"/g, '""')}"`;

/**
 * The full leads list. Lead Management shows a preview of the most recent
 * leads alongside the platform cards; this is where "View All Leads" goes for
 * the complete, searchable list. Filtering and pagination are server-side
 * here — unlike the platform screens, which aggregate one page client-side.
 */
const AllLeads = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [leads, setLeads] = useState<MarketingLead[]>([]);
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);

  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [platform, setPlatform] = useState<AdPlatform | ''>(
    (searchParams.get('source') as AdPlatform) || '',
  );
  const [campaignId, setCampaignId] = useState('');
  const [status, setStatus] = useState<MarketingLeadStatus | ''>('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  // Debounced so typing doesn't fire a request per keystroke.
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
      setPage(0);
    }, 350);
    return () => clearTimeout(timer);
  }, [search]);

  const fetchLeads = useCallback(async () => {
    try {
      setLoading(true);
      const result = await getMarketingLeads({
        page: page + 1,
        limit: rowsPerPage,
        search: debouncedSearch || undefined,
        source: platform || undefined,
        campaignId: campaignId || undefined,
        status: status || undefined,
      });
      setLeads(result.data);
      setTotal(result.total);
    } catch (error) {
      console.error('Failed to load leads:', error);
      setLeads([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, debouncedSearch, platform, campaignId, status]);

  useEffect(() => {
    fetchLeads();
  }, [fetchLeads]);

  useEffect(() => {
    getMarketingLeadCampaigns()
      .then(setCampaigns)
      .catch((error) => {
        console.error('Failed to load campaigns:', error);
        setCampaigns([]);
      });
  }, []);

  const handleExport = () => {
    const header = ['Name', 'Phone', 'Email', 'Source', 'Campaign', 'Received On', 'Status'];
    const rows = leads.map((lead) => [
      lead.name,
      lead.phoneNumber,
      lead.email,
      lead.platform,
      lead.campaignName || 'Uncategorized',
      dayjs(lead.receivedOn).format('YYYY-MM-DD HH:mm'),
      lead.status,
    ]);

    const csv = [header, ...rows].map((row) => row.map(escapeCsvCell).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `leads-page-${page + 1}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, pb: 3 }}>
      <PageHeaderBar showDivider>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <IconButton
            size="small"
            onClick={() => navigate('/leads')}
            aria-label="Back to lead management"
            sx={{ color: '#e2e8f0' }}
          >
            <ArrowBackIosNewIcon sx={{ fontSize: 16 }} />
          </IconButton>
          <Box>
            <Typography sx={appShellHeaderTitleSx}>All Leads</Typography>
            <Typography sx={{ fontSize: 12, color: dashboardTokens.textSecondary, mt: 0.25 }}>
              {loading ? 'Loading…' : `${total} lead${total === 1 ? '' : 's'} across all sources`}
            </Typography>
          </Box>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
          <IconButton
            size="small"
            onClick={handleExport}
            aria-label="Export this page as CSV"
            sx={{
              width: 36,
              height: 36,
              bgcolor: '#181818',
              border: `1px solid ${dashboardTokens.border}`,
              color: '#e2e8f0',
              borderRadius: 1.5,
              '&:hover': { bgcolor: '#232323' },
            }}
          >
            <FileDownloadOutlinedIcon sx={{ fontSize: 18 }} />
          </IconButton>
          <AdminAvatar />
        </Box>
      </PageHeaderBar>

      <Box sx={{ px: 2 }}>
        <Box
          sx={{
            bgcolor: dashboardTokens.cardBg,
            border: `1px solid ${dashboardTokens.border}`,
            borderRadius: dashboardTokens.cardRadius,
            p: 2.5,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 1.5,
              mb: 2,
              flexWrap: 'wrap',
            }}
          >
            <TextField
              size="small"
              placeholder="Search name, email or phone"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ fontSize: 18, color: '#94a3b8' }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                minWidth: 280,
                '& .MuiOutlinedInput-root': {
                  bgcolor: '#1c1c1c',
                  color: '#e2e8f0',
                  fontSize: 13.5,
                  '& fieldset': { borderColor: dashboardTokens.border },
                },
              }}
            />

            <Box sx={{ display: 'flex', gap: 1.25, flexWrap: 'wrap' }}>
              <FormControl size="small">
                <Select
                  displayEmpty
                  value={platform}
                  onChange={(e) => {
                    setPlatform(e.target.value as AdPlatform | '');
                    setPage(0);
                  }}
                  sx={selectSx}
                >
                  <MenuItem value="">All sources</MenuItem>
                  {PLATFORM_OPTIONS.map((p) => (
                    <MenuItem key={p} value={p}>
                      {platformMeta[p].label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl size="small">
                <Select
                  displayEmpty
                  value={campaignId}
                  onChange={(e) => {
                    setCampaignId(e.target.value);
                    setPage(0);
                  }}
                  sx={selectSx}
                >
                  <MenuItem value="">All campaigns</MenuItem>
                  {campaigns.map((c) => (
                    <MenuItem key={c._id} value={c._id}>
                      {c.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl size="small">
                <Select
                  displayEmpty
                  value={status}
                  onChange={(e) => {
                    setStatus(e.target.value as MarketingLeadStatus | '');
                    setPage(0);
                  }}
                  sx={selectSx}
                >
                  <MenuItem value="">All statuses</MenuItem>
                  {STATUS_OPTIONS.map((s) => (
                    <MenuItem key={s} value={s}>
                      {leadStatusMeta[s].label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          </Box>

          <Box sx={{ overflowX: 'auto' }}>
            <LeadsListTable
              leads={leads}
              isLoading={loading}
              noDataText="No leads match these filters."
              onConverted={fetchLeads}
            />
          </Box>

          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
            <Pagination
              count={total}
              page={page}
              rowsPerPage={rowsPerPage}
              onPageChange={(_: unknown, newPage: number) => setPage(newPage)}
              onRowsPerPageChange={(event) => {
                setRowsPerPage(parseInt(event.target.value, 10));
                setPage(0);
              }}
            />
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default withComponentAuth(AllLeads);
